<!-- Responsibility: Render and submit the floating feedback UI.
Scope: Owns feedback form state and delegates submission work. -->
<feedback-form>
  <div class="feedback-container { state.active ? 'active' : '' }">
    <button if={ !state.active } class="feedback-toggle tooltip-wrap" onclick={ toggle } aria-label="Provide feedback">
      <span>💬 FEEDBACK</span>
      <div class="tooltip" data-position="left" data-tip-custom="true">Share your thoughts or report a bug</div>
    </button>

    <div if={ state.active } class="feedback-panel">
      <header>
        <strong>FEEDBACK</strong>
        <button type="button" class="close-btn" onclick={ toggle }>✕</button>
      </header>
      
      <p class="feedback-hint">Share your thoughts, report bugs, or suggest improvements. System state will be included for context.</p>
      
      <textarea
        ref="text"
        placeholder="How is your experience so far?"
        onkeydown={ handleKey }
        disabled={ state.busy }
      ></textarea>

      <footer>
        <span class="status-note { state.error ? 'error' : '' }">{ state.status }</span>
        <button class="action-btn primary compact" onclick={ submit } disabled={ state.busy }>
          { state.busy ? 'SENDING...' : 'SEND' }
        </button>
      </footer>
    </div>
  </div>

  <style>
    .feedback-container {
      position: fixed;
      right: var(--space-md);
      bottom: var(--space-md);
      z-index: var(--z-feedback, 1300);
      display: flex;
      flex-direction: column;
      align-items: flex-end;
    }

    .feedback-toggle {
      background: color-mix(in srgb, var(--surface) 80%, black);
      border: 1px solid var(--gold-glow);
      color: var(--gold);
      padding: 0.6rem var(--space-md);
      border-radius: 62.4rem;
      font-size: var(--type-3xs);
      font-weight: 800;
      letter-spacing: 0.1em;
      cursor: pointer;
      box-shadow: var(--shadow-elev-2);
      transition: all 0.2s;
      &:hover { border-color: var(--gold); background: var(--surface); transform: translateY(-1px); }
    }

    .feedback-panel {
      width: 18rem;
      background: color-mix(in srgb, var(--surface) 96%, black);
      border: 1px solid var(--border);
      border-radius: var(--space-md);
      padding: var(--space-md);
      box-shadow: var(--shadow-elev-3);
      display: grid;
      gap: var(--space-sm);
      animation: feedback-slide-in 0.24s ease-out;
    }

    .feedback-panel header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      strong { font-size: 0.75rem; letter-spacing: 0.15em; color: var(--gold); }
    }

    .close-btn {
      background: transparent;
      border: none;
      color: var(--text-muted);
      cursor: pointer;
      font-size: 1rem;
      &:hover { color: var(--text-prose); }
    }

    .feedback-hint {
      font-size: var(--type-3xs);
      color: var(--text-muted);
      line-height: 1.4;
      margin: 0;
    }

    .feedback-panel textarea {
      width: 100%;
      min-height: 6rem;
      background: var(--bg);
      border: 1px solid var(--border);
      border-radius: var(--space-xs);
      color: var(--text-prose);
      padding: var(--space-sm);
      font: var(--font-ui);
      font-size: 0.85rem;
      resize: none;
      &:focus { border-color: var(--gold-glow); outline: none; }
    }

    .feedback-panel footer {
      display: flex;
      justify-content: space-between;
      align-items: center;
    }

    .status-note {
      font-size: var(--type-3xs);
      color: var(--text-muted);
      &.error { color: var(--danger); }
    }
  </style>

  <script>
    import { playEffect } from '../../engine/audio.js'
    import { dispatcher } from '../../app-states.js'
    import { TOPICS } from "@anyquest/shared-core"
    import { emailService } from '../../services/email-service.ts'

    export default {
      state: {
        active: false,
        busy: false,
        status: '',
        error: false,
      },

      onMounted() {
        this.unmounted = false
        this.handle = dispatcher.on(TOPICS.system.feedbackRequest, () => {
          if (!this.unmounted && !this.state.active) this.toggle()
        })
      },

      onUnmounted() {
        this.unmounted = true
        if (this.handle) dispatcher.off(this.handle)
        if (this.statusTimer) clearTimeout(this.statusTimer)
      },

      toggle() {
        if (this.unmounted) return
        playEffect('ui_click')
        this.update({ active: !this.state.active, status: '', error: false })
        if (this.state.active) {
          requestAnimationFrame(() => {
            if (!this.unmounted) this.$('textarea')?.focus()
          })
        }
      },

      handleKey(e) {
        if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) this.submit()
      },

      async submit() {
        if (this.unmounted) return
        const text = this.$('textarea')?.value?.trim()
        if (!text || text.length < 3) {
          this.update({ status: 'Too short.', error: true })
          return
        }

        playEffect('ui_click')
        this.update({ busy: true, status: 'Sending...', error: false })

        const result = await emailService.submitFeedback(text)
        if (this.unmounted) return

        this.update({
          busy: false,
          status: result.message,
          error: result.error,
          active: result.ok ? false : this.state.active,
        })

        if (this.statusTimer) clearTimeout(this.statusTimer)
        if (result.ok) {
          this.statusTimer = setTimeout(() => {
            if (!this.unmounted) this.update({ status: '' })
          }, 3000)
        }
      }
    }
  </script>
</feedback-form>
